import { FC, useEffect, useState } from "react";
import {
  Button,
  InputGroup,
  InputSelect,
  NO_REGEX,
  PhoneInputGroup,
  color,
} from "@funded-here-interface/common";
import { useNavigate } from "react-router-dom";
import { useStyles } from "../../styles/BizProfileCss";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "@funded-here-interface/investor-onboarding/src/store";
import {
  countries,
  CountryCode,
  countryNameToCodeMap,
} from "@funded-here-interface/common/src/constant/country";
import {
  LegalStructures,
  IndustryGroupMap,
  LegalStructuresInvestor,
  LegalStructuresMerchantAndSPV,
} from "../../constants/dropdownValues";
import { Industry, IndustryGroup, LegalStructure } from "../../constants/enums";
import OnboardingLayout from "../../layouts/OnboardingLayout/OnboardingLayout";
import { Persona } from "@funded-here-interface/common/src/constant/enum";
import { useDisclosure } from "@mantine/hooks";
import InvestorDeclarationModal from "./Components/InvestorDeclarationModal/InvestorDeclarationModal";
import { toast } from "react-toastify";
import { camelCaseToNormalCase } from "../../Utils/formatter";
import useGetOrgDraft from "../../hooks/useGetOrgDraft";
import useSaveOrgDraft from "../../hooks/useSaveOrgDraft";
import { initializeBusinessDetails } from "../../Utils/dispatchInstitutional";
import { formatDateDraft } from "@funded-here-interface/common/src/Utils/formatter";
import { isValidPhoneNumber } from "react-phone-number-input";
import {
  getIndonesiaStateOptions,
  getStateIdByName,
} from "@funded-here-interface/common/src/constant/indonesia";
import { useGetIndonesiaCitiesQuery } from "@funded-here-interface/common/src/hooks/useGetIndonesiaCities";
import { IndonesiaCity } from "@funded-here-interface/common/src/services/getIndonesiaCities";

const initialFormState = {
  companyName: "",
  companyRegistrationNo: "",
  legalStructure: "",
  legalStructureOthers: "",
  countryOfIncorporation: "",
  countryOfDomicile: "",
  region: "",
  city: "",
  legalEntityAddress: "",
  postalCode: "",
  website: "",
  phoneNumber: "",
  industryGroup: "",
  industry: "",
  industryOthers: "",
  investmentAmount: 0,
  companyStartDate: "",
};

const initialErrorState = Object.keys(initialFormState).reduce(
  (acc, key) => {
    acc[key] = "";
    return acc;
  },
  {} as Record<string, string>
);

const BizProfile: FC = () => {
  const { classes } = useStyles();
  const Navigate = useNavigate();
  const type = useSelector((state: RootState) => state.auth.type);
  const isInvestor = type === Persona.INVESTOR;
  const investorDeclarationQualified = useSelector(
    (state: RootState) => state.biz.investorDeclarationQualified
  );
  const [opened, { close }] = useDisclosure(
    isInvestor && !investorDeclarationQualified
  );
  const dispatch = useDispatch();
  const userId = useSelector((state: RootState) => state.auth.userId);
  const { token } = useSelector((state: RootState) => state.auth);

  // Form state
  const [form, setForm] = useState({ ...initialFormState });
  const [error, setError] = useState({ ...initialErrorState });
  const [industryDropdownValues, setIndustryDropdownValues] = useState<
    Industry[]
  >([]);
  const [countryCode, setCountryCode] = useState<CountryCode | undefined>();
  const [initPage, setInitPage] = useState(true);

  // State for Indonesia region selection
  const [selectedStateId, setSelectedStateId] = useState<string | null>(null);

  const getOrgDraftMutation = useGetOrgDraft();
  const saveOrgDraftMutation = useSaveOrgDraft();

  // countryOfDomicile — institutional investor business domicile
  const isIndonesia = form.countryOfDomicile === "Indonesia";

  // Indonesia cities query with caching - only fetches when stateId changes
  const { data: citiesData, isLoading: isCitiesLoading } =
    useGetIndonesiaCitiesQuery({
      stateId: selectedStateId,
      token,
    });

  // Derive city options from cached query data
  const cityOptions = citiesData?.map((c: IndonesiaCity) => c.value) || [];

  // Populate form from draft
  const populateFormFromDraft = (data: any) => {
    setForm({
      companyName: data.companyName || "",
      companyRegistrationNo: data.companyRegistrationNo || "",
      legalStructure: data.legalStructure || "",
      legalStructureOthers: data.legalStructureOthers || "",
      countryOfIncorporation: data.countryOfIncorporation || "",
      countryOfDomicile: data.countryOfDomicile || "",
      region: data.region || "",
      city: data.city || "",
      legalEntityAddress: data.legalEntityAddress || "",
      postalCode: data.postalCode ? String(data.postalCode) : "",
      website: data.website || "",
      phoneNumber: data.phoneNumber || "",
      industryGroup: data.industryGroup || "",
      industry: data.industry || "",
      industryOthers: data.industryOthers || "",
      investmentAmount: Number(data.investmentAmount) || 0,
      companyStartDate: formatDateDraft(data.companyStartDate),
    });

    if (data.countryOfDomicile) {
      const code = countryNameToCodeMap.get(data.countryOfDomicile);
      setCountryCode(code as CountryCode);
    }
    setIndustryDropdownValues(
      IndustryGroupMap[data.industryGroup as IndustryGroup] || []
    );

    // If Indonesia and region is set, set stateId to trigger cached city fetch
    if (data.countryOfDomicile === "Indonesia" && data.region) {
      const stateId = getStateIdByName(data.region);
      setSelectedStateId(stateId || null);
    }
  };

  // Fetch draft on mount
  useEffect(() => {
    const fetchDraft = async () => {
      if (initPage) {
        try {
          const params = { userId: parseInt(userId), token };
          const data = await getOrgDraftMutation.mutateAsync(params);
          if (data) populateFormFromDraft(data);
        } catch (error) {
          toast.error(`Error fetching draft: ${error}`);
        } finally {
          setInitPage(false);
        }
      }
    };
    fetchDraft();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initPage, userId, token]);

  // Redirect if not qualified
  const investorDeclarationQualifiedSelector = useSelector(
    (state: RootState) => state.biz.investorDeclarationQualified
  );

  useEffect(() => {
    if (investorDeclarationQualifiedSelector === false) {
      Navigate("/unqualified-investor");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [investorDeclarationQualifiedSelector]);

  // Generic onChange for text fields
  const handleFieldChange = (field: string, value: any) => {
    // Prevent future dates for date fields
    if (field === "companyStartDate" && value && new Date(value) > new Date()) {
      setError((prev) => ({
        ...prev,
        [field]: "Date of Incorporation cannot be a future date",
      }));
      return;
    }
    setForm((prev) => ({ ...prev, [field]: value }));
    setError((prev) => ({ ...prev, [field]: "" }));
  };

  // Special handlers
  const handleLegalStructureChange = (value: LegalStructure) => {
    setForm((prev) => ({
      ...prev,
      legalStructure: value,
      legalStructureOthers: "",
    }));
    setError((prev) => ({
      ...prev,
      legalStructure: "",
      legalStructureOthers: "",
    }));
  };

  const handleIndustryGroupChange = (value: IndustryGroup) => {
    setForm((prev) => ({ ...prev, industryGroup: value, industry: "" }));
    setIndustryDropdownValues(IndustryGroupMap[value] || []);
    setError((prev) => ({ ...prev, industryGroup: "", industry: "" }));
  };

  const handleCountryOfDomicileChange = (value: string) => {
    setForm((prev) => ({
      ...prev,
      countryOfDomicile: value,
      region: "",
      city: "",
    }));
    setError((prev) => ({
      ...prev,
      countryOfDomicile: "",
      region: "",
      city: "",
    }));
    setSelectedStateId(null);
    const code = countryNameToCodeMap.get(value);
    setCountryCode(code as CountryCode);
  };

  const handleRegionChange = (value: string) => {
    setForm((prev) => ({ ...prev, region: value, city: "" }));
    setError((prev) => ({ ...prev, region: "", city: "" }));
    const stateId = getStateIdByName(value);
    setSelectedStateId(stateId || null);
  };

  const handleCityChange = (value: string) => {
    setForm((prev) => ({ ...prev, city: value }));
    setError((prev) => ({ ...prev, city: "" }));
  };

  // Validation
  const validateForm = () => {
    let valid = true;
    const customError: typeof error = { ...initialErrorState };

    // Custom error messages for specific fields
    const customMessages: Record<string, string> = {
      companyRegistrationNo: "Registration Number is required",
      companyStartDate: "Date of Incorporation is required",
      legalEntityAddress: "Address is required",
    };

    // Fields that are conditionally required or optional
    const optionalFields = [
      "legalStructureOthers",
      "industryOthers",
      "investmentAmount",
    ];
    // Region and city are only required for Indonesia
    if (!isIndonesia) {
      optionalFields.push("region", "city");
    }

    Object.entries(form).forEach(([key, value]) => {
      if (!optionalFields.includes(key) && !value) {
        valid = false;
        customError[key] =
          customMessages[key] || `${camelCaseToNormalCase(key)} is required`;
      }
    });
    if (isInvestor && !form.investmentAmount) {
      valid = false;
      customError.investmentAmount = "Planned Investment Amount is required";
    }

    if (!isValidPhoneNumber(String(form.phoneNumber))) {
      valid = false;
      customError.phoneNumber = "Phone Number is not valid";
    }

    // Validate date of incorporation is not in the future
    if (form.companyStartDate && new Date(form.companyStartDate) > new Date()) {
      valid = false;
      customError.companyStartDate =
        "Date of Incorporation cannot be a future date";
    }

    setError(customError);
    return valid;
  };

  // Save draft
  const saveDraft = (showToast: boolean = true) => {
    // If country is not Indonesia, explicitly clear region and city
    const clearFields: ("region" | "city")[] = !isIndonesia
      ? ["region", "city"]
      : [];

    saveOrgDraftMutation.mutateAsync(
      {
        token,
        userId: parseInt(userId),
        ...form,
        companyPhoneNumber: form.phoneNumber,
        postalCode: form.postalCode || null,
        companyStartDate: form.companyStartDate ? form.companyStartDate : null,
        clearFields,
      },
      {
        onSuccess: () => {
          if (showToast) toast.success("Draft Saved");
        },
        onError: (e) => {
          toast.error((e as Error).message);
        },
      }
    );
  };

  // Submit handler
  const handleSubmit = async () => {
    if (validateForm()) {
      await saveDraft(false);
      initializeBusinessDetails(dispatch, form, industryDropdownValues);

      if (isInvestor) {
        Navigate("/institutional-rep");
      } else {
        Navigate("/representative-details");
      }
    }
  };

  return (
    <OnboardingLayout
      step={1}
      showSteps={true}
      content={
        <>
          <InvestorDeclarationModal
            onError={toast.error}
            opened={opened}
            close={close}
          />
          <div style={{ marginTop: "70px" }}>
            <InputGroup
              isErrored={error.companyName}
              errorMessage
              onChange={(value: string) =>
                handleFieldChange("companyName", value)
              }
              labelText="Company Name"
              id="companyName"
              value={form.companyName}
            />
            <InputGroup
              isErrored={error.companyRegistrationNo}
              errorMessage
              onChange={(value: string) =>
                handleFieldChange("companyRegistrationNo", value)
              }
              labelText="Registration Number"
              id="registrationNumber"
              value={form.companyRegistrationNo}
            />
            <InputGroup
              isErrored={error.companyStartDate}
              errorMessage
              onChange={(value: any) =>
                handleFieldChange("companyStartDate", value)
              }
              labelText="Date of Incorporation"
              value={form.companyStartDate}
              id="incorporationDate"
              type="date"
              max={new Date().toISOString().split("T")[0]}
            />
            <InputSelect
              isErrored={error.legalStructure}
              errorMessage
              onChange={handleLegalStructureChange}
              labelText="Legal Structure"
              id="legalStructure"
              value={form.legalStructure}
              data={
                !isInvestor
                  ? LegalStructuresMerchantAndSPV
                  : LegalStructuresInvestor
              }
            />
            <InputSelect
              isErrored={error.industryGroup}
              errorMessage
              onChange={handleIndustryGroupChange}
              labelText="Industry Group"
              value={form.industryGroup}
              id="industryGroup"
              data={Object.values(IndustryGroup).sort((a, b) =>
                a.localeCompare(b)
              )}
            />
            <InputSelect
              isErrored={error.industry}
              errorMessage
              onChange={(value: Industry) =>
                handleFieldChange("industry", value)
              }
              labelText="Industry"
              value={form.industry}
              id="industry"
              data={[...industryDropdownValues].sort((a, b) =>
                a.localeCompare(b)
              )}
            />
            <InputSelect
              isErrored={error.countryOfIncorporation}
              errorMessage
              onChange={(value: string) =>
                handleFieldChange("countryOfIncorporation", value)
              }
              labelText="Country of Incorporation"
              id="countryOfIncorporation"
              value={form.countryOfIncorporation}
              data={countries}
            />
            <InputSelect
              isErrored={error.countryOfDomicile}
              errorMessage
              onChange={handleCountryOfDomicileChange}
              labelText="Country of Domicile"
              id="countryOfDomicile"
              value={form.countryOfDomicile}
              data={countries}
            />
            {isIndonesia && (
              <>
                <InputSelect
                  isErrored={error.region}
                  errorMessage
                  onChange={handleRegionChange}
                  labelText="Region"
                  id="region"
                  value={form.region}
                  data={getIndonesiaStateOptions()}
                />
                <InputSelect
                  isErrored={error.city}
                  errorMessage
                  onChange={handleCityChange}
                  labelText={isCitiesLoading ? "City (Loading...)" : "City"}
                  id="city"
                  value={form.city}
                  data={cityOptions}
                  disabled={!form.region || isCitiesLoading}
                />
              </>
            )}
            <InputGroup
              isErrored={error.legalEntityAddress}
              errorMessage
              onChange={(value: string) =>
                handleFieldChange("legalEntityAddress", value)
              }
              labelText="Address"
              id="address"
              value={form.legalEntityAddress}
            />
            <InputGroup
              isErrored={error.postalCode}
              errorMessage
              onChange={(value: string) =>
                handleFieldChange("postalCode", value.replace(NO_REGEX, ""))
              }
              labelText="Postal Code"
              id="postalcode"
              value={form.postalCode}
            />
            <InputGroup
              isErrored={error.website}
              errorMessage
              onChange={(value: string) => handleFieldChange("website", value)}
              labelText="Website"
              id="companywebsite"
              value={form.website}
            />
            {isInvestor && (
              <InputGroup
                isErrored={error.investmentAmount}
                errorMessage
                onChange={(value: string) =>
                  handleFieldChange(
                    "investmentAmount",
                    parseInt(value.replace(NO_REGEX, "")) || 0
                  )
                }
                labelText="Planned Investment Amount"
                id="investmentAmount"
                value={`${form.investmentAmount || 0}`}
              />
            )}
            <div className={classes.phoneNumberWrapper}>
              <PhoneInputGroup
                isErrored={error.phoneNumber}
                value={form.phoneNumber}
                country={countryCode}
                onChange={(value: string) =>
                  handleFieldChange("phoneNumber", value)
                }
              />
            </div>
            <div className={classes.buttonWrapper}>
              <Button
                onClick={handleSubmit}
                textColor={color.WHITE}
                backgroundColor={color.FHGREEN}
                width="100%"
              >
                Next
              </Button>
            </div>
            <div className={classes.buttonWrapper}>
              <Button
                onClick={() => saveDraft()}
                textColor={color.WHITE}
                backgroundColor={color.LOANTEXT}
                width="100%"
              >
                Save Draft
              </Button>
            </div>
          </div>
        </>
      }
    />
  );
};

export default BizProfile;
